<template>
  <div class="grid-card-field" :class="campoClasses" :style="cssVars">
    <label class="grid-card-field__label">{{ label }}</label>
    <strong class="grid-card-field__valor">
      <slot>{{ valorFormatado }}</slot>
    </strong>
  </div>
</template>

<script>
import { monetary } from "@/filters/monetary";
import { dateFilter } from "@/filters/dateFilter";

const TIPOS_VALIDOS = [
  "text",
  "monetary",
  "percent",
  "integer",
  "decimal",
  "numero",
  "date",
  "datetime",
  "boolean",
];

/**
 * Campo simples (label + valor formatado).
 *
 * Tipos suportados:
 *  - text (default)
 *  - monetary  (R$ 1.234,56)
 *  - percent   (12,50%)
 *  - integer   (1.234)
 *  - decimal   (1.234,56)
 *  - date      (dd/mm/aaaa)
 *  - datetime  (dd/mm/aaaa hh:mm:ss)
 *  - boolean   (Sim / Não)
 *
 * O slot default sobrescreve a renderização do valor (útil para conteúdo customizado).
 *
 * Exemplo:
 *  <GridCardField label="CMV" :valor="12.50" tipo="monetary" />
 *  <GridCardField label="Atualizado" :valor="produto.updated_at" tipo="datetime" />
 *  <GridCardField label="Cor" destaque>
 *    <span :style="{ background: produto.hexa }" class="swatch"></span>
 *    {{ produto.cor }}
 *  </GridCardField>
 */
export default {
  name: "GridCardField",
  props: {
    label: { type: String, required: true },
    valor: { type: [String, Number, Boolean, Date, Object], default: null },
    tipo: {
      type: String,
      default: "text",
      validator: (valor) => TIPOS_VALIDOS.includes(valor),
    },
    destaque: { type: Boolean, default: false },
    cols: { type: [Number, String], default: null },
    placeholder: { type: String, default: "—" },
  },
  computed: {
    campoClasses() {
      return {
        "grid-card-field--destaque": this.destaque,
      };
    },
    cssVars() {
      if (this.cols === null) return null;
      const colsNumero = Math.max(1, Math.min(12, Number(this.cols)));
      return { "grid-column": `span ${colsNumero}` };
    },
    valorFormatado() {
      const valor = this.valor;
      if (valor === null || valor === undefined || valor === "") {
        return this.placeholder;
      }

      switch (this.tipo) {
        case "monetary":
          return monetary(valor);
        case "percent": {
          const numero = Number(valor);
          if (Number.isNaN(numero)) return this.placeholder;
          return `${numero.toFixed(2)}%`;
        }
        case "integer":
          return Number(valor).toLocaleString("pt-BR", { maximumFractionDigits: 0 });
        case "decimal":
          return Number(valor).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        case "numero":
          // Mostra inteiros sem casas decimais e fracionários até 4 casas
          // (remove zeros à direita: 30.0000 -> "30"; 1.5 -> "1,5")
          return Number(valor).toLocaleString("pt-BR", {
            maximumFractionDigits: 4,
          });
        case "date":
          return dateFilter(valor);
        case "datetime":
          return dateFilter(valor, "DD/MM/YYYY HH:mm:ss");
        case "boolean":
          return Number(valor) === 1 || valor === true ? "Sim" : "Não";
        case "text":
        default:
          return String(valor);
      }
    },
  },
};
</script>

<style scoped>
.grid-card-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #f2f4f7;
  border-radius: 6px;
}

.grid-card-field--destaque {
  background: linear-gradient(135deg, rgba(58, 62, 129, 0.08), rgba(34, 152, 163, 0.08));
  border-color: #c5e6e9;
}

.grid-card-field__label {
  font-size: 10px;
  font-weight: 600;
  color: #98a2b3;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0;
}

.grid-card-field__valor {
  font-size: 13px;
  color: #1d2939;
  font-weight: 600;
  font-feature-settings: "tnum";
}

.grid-card-field--destaque .grid-card-field__valor {
  color: #3a3e81;
  font-size: 15px;
}
</style>
